/** Otomatik yatırım bonusunun oyuncuya ve panele gösterilen kuralları.
 *
 * Asıl karar sunucudadır (services/bonus_rules.py → saat penceresi, kademe
 * seçimi, freespin ya da bakiye). Buradaki fonksiyonlar yalnızca
 * **anlatır**: kart, detay penceresi ve paneldeki önizleme aynı metni
 * üretsin diye tek yerdedir.
 */

const formatTl = (value) => `${Number(value || 0).toLocaleString('tr-TR')} ₺`;

export const REWARD_FREESPIN = 'freespin';
export const REWARD_CASH = 'cash';

/** Otomatik kampanyanın ödülü: yalnızca para ödülü açıksa bakiye.
 *
 * Ayrı bir alan yoktur; sunucu da aynı iki bayraktan okur
 * (services/bonus_auto.reward_kind). Otomatik kampanyada ikisi birlikte
 * açık olamaz. */
export const tierKind = (bonus) => (
  bonus?.cash_reward_enabled !== false && !bonus?.freespin_enabled
    ? REWARD_CASH
    : REWARD_FREESPIN
);

/** Satırın o türdeki değeri. Tür değiştirildiğinde öteki alan okunur ki
 * yazılmış tablo kaybolmasın (1.000 ₺ → 100 spin, 1.000 ₺ → 100 ₺ olur). */
export const tierValue = (row, kind) => (
  kind === REWARD_CASH
    ? (row?.amount ?? row?.count ?? '')
    : (row?.count ?? row?.amount ?? '')
);

/** Sunucuya gidecek satır: yalnızca türün alanı. */
export const serializeTier = (row, kind) => (
  kind === REWARD_CASH
    ? { min_deposit: Number(row?.min_deposit), amount: Number(tierValue(row, kind)) }
    : { min_deposit: Number(row?.min_deposit), count: Number(tierValue(row, kind)) }
);

/** Kademe tablosu, alt sınıra göre sıralı ve temizlenmiş.
 *
 * Satırın ``value`` alanı türün ödülüdür (spin adedi ya da ₺); ``count``
 * freespin satırında, ``amount`` para satırında dolar. */
export const bonusTiers = (bonus) => {
  const kind = tierKind(bonus);
  return (Array.isArray(bonus?.freespin_tiers) ? bonus.freespin_tiers : [])
    .map((row) => {
      const minimum = Number(row?.min_deposit) || 0;
      if (kind === REWARD_CASH) {
        const amount = Math.round((Number(row?.amount) || 0) * 100) / 100;
        return { min_deposit: minimum, value: amount, amount };
      }
      const count = Math.trunc(Number(row?.count) || 0);
      return { min_deposit: minimum, value: count, count };
    })
    .filter((row) => row.min_deposit > 0 && row.value > 0)
    .sort((a, b) => a.min_deposit - b.min_deposit);
};

/** Tek kademenin ödül metni: "100 Freespin" ya da "100 ₺ Bonus". */
export const tierRewardText = (value, kind) => (
  kind === REWARD_CASH
    ? `${formatTl(value)} Bonus`
    : `${Number(value || 0).toLocaleString('tr-TR')} Freespin`
);

export const isAutoBonus = (bonus) => !!bonus?.auto_award;

/** "20:00 – 23:59" ya da boş string (saat sınırı yok). */
export const bonusWindowText = (bonus) => {
  const start = String(bonus?.deposit_window_start || '').trim();
  const end = String(bonus?.deposit_window_end || '').trim();
  return start && end ? `${start} – ${end}` : '';
};

/** Kademeli kampanyada "100 – 1.000 Freespin" ya da "100 ₺ – 1.000 ₺ Bonus";
 * tek kademede tek değer. */
export const tierRangeText = (bonus) => {
  const tiers = bonusTiers(bonus);
  if (!tiers.length) return '';
  const kind = tierKind(bonus);
  const values = tiers.map((row) => row.value);
  const low = Math.min(...values);
  const high = Math.max(...values);
  if (low === high) return tierRewardText(low, kind);
  return kind === REWARD_CASH
    ? `${formatTl(low)} – ${formatTl(high)} Bonus`
    : `${low.toLocaleString('tr-TR')} – ${high.toLocaleString('tr-TR')} Freespin`;
};

/** Hak doğuran en düşük yatırım: kademe varsa ilk satır, yoksa kampanyanınki. */
export const bonusMinDeposit = (bonus) => {
  const tiers = bonusTiers(bonus);
  return tiers.length ? tiers[0].min_deposit : Number(bonus?.min_deposit) || 0;
};

/** Kademeli para kampanyasının kartındaki "Max. Bonus": en üst kademe.
 * Kademesiz kampanyada kampanyanın kendi Max. Bonus değeri. */
export const bonusMaxBonus = (bonus) => {
  const tiers = bonusTiers(bonus);
  if (tiers.length && tierKind(bonus) === REWARD_CASH) {
    return Math.max(...tiers.map((row) => row.value));
  }
  return Number(bonus?.max_bonus) || 0;
};

/** Detay penceresindeki kademe satırları: "1.000 ₺ ve üzeri → 100 Freespin". */
export const tierLines = (bonus) => {
  const kind = tierKind(bonus);
  return bonusTiers(bonus).map((row, index, all) => {
    const next = all[index + 1];
    const range = next
      ? `${formatTl(row.min_deposit)} – ${formatTl(next.min_deposit - 0.01)}`
      : `${formatTl(row.min_deposit)} ve üzeri`;
    return {
      range, value: row.value, kind, label: tierRewardText(row.value, kind),
    };
  });
};

/** Panelin "örnek tabloyu doldur" düğmesi: 1.000 ₺ → 100 … 10.000 ₺ → 1.000
 * (freespin'de spin, bakiyede ₺). */
export const sampleTiers = (kind = REWARD_FREESPIN) => Array.from(
  { length: 10 },
  (_, index) => (kind === REWARD_CASH
    ? { min_deposit: (index + 1) * 1000, amount: (index + 1) * 100 }
    : { min_deposit: (index + 1) * 1000, count: (index + 1) * 100 }),
);

// Sunucudaki sınırlar (services/bonus_rules.py): MAX_TIER_COUNT / _AMOUNT.
const MAX_TIER_COUNT = 10000;
const MAX_TIER_AMOUNT = 1000000;

/** Panel formunun kademe denetimi; hata metni ya da boş string. */
export const tierProblem = (tiers, kind = REWARD_FREESPIN) => {
  const seen = new Set();
  for (const row of tiers || []) {
    const minimum = Number(row?.min_deposit);
    const value = Number(tierValue(row, kind));
    if (!(minimum > 0)) return 'Her kademenin yatırım tutarı sıfırdan büyük olmalıdır.';
    if (kind === REWARD_CASH) {
      if (!(value > 0) || value > MAX_TIER_AMOUNT) {
        return 'Her kademenin bonus tutarı 0 ile 1.000.000 ₺ arasında olmalıdır.';
      }
    } else if (!Number.isInteger(value) || value < 1 || value > MAX_TIER_COUNT) {
      return 'Her kademenin spin adedi 1-10.000 arasında bir tam sayı olmalıdır.';
    }
    if (seen.has(minimum)) return `${formatTl(minimum)} kademesi iki kez yazılmış.`;
    seen.add(minimum);
  }
  return '';
};

// --- Kampanyanın uygunluk kuralları (services/bonus_eligibility.py) ---

/** ISO gün numaraları (1 = Pazartesi); sunucu aynı numaraları saklar. */
export const WEEKDAYS = [
  { id: 1, short: 'Pzt', label: 'Pazartesi' },
  { id: 2, short: 'Sal', label: 'Salı' },
  { id: 3, short: 'Çar', label: 'Çarşamba' },
  { id: 4, short: 'Per', label: 'Perşembe' },
  { id: 5, short: 'Cum', label: 'Cuma' },
  { id: 6, short: 'Cmt', label: 'Cumartesi' },
  { id: 7, short: 'Paz', label: 'Pazar' },
];

export const parseWeekdays = (value) => String(value || '')
  .split(',')
  .map((part) => Number(part.trim()))
  .filter((day) => day >= 1 && day <= 7);

/** Seçili günler; hiçbiri ya da yedisi birden seçiliyse '' (her gün). */
export const serializeWeekdays = (days) => {
  const unique = [...new Set(days)].sort((a, b) => a - b);
  return unique.length === 0 || unique.length === 7 ? '' : unique.join(',');
};

export const weekdaysText = (value) => {
  const days = parseWeekdays(value);
  if (!days.length) return '';
  if (serializeWeekdays(days) === '1,2,3,4,5') return 'Hafta içi';
  if (serializeWeekdays(days) === '6,7') return 'Hafta sonu';
  return WEEKDAYS.filter((day) => days.includes(day.id)).map((day) => day.short).join(', ');
};

/** Kolonlar eklenmeden önce kaydedilmiş kampanyada iki kilit açık sayılır. */
export const ruleFlag = (bonus, key) => (
  bonus?.[key] === undefined || bonus?.[key] === null
    ? key === 'exclusive_deposit' || key === 'blocks_loss_bonus'
    : !!bonus[key]
);

/** Kampanyanın uygunluk kurallarının kısa satırları (panel kartı). */
// İade/kayıp kampanyasında "kaybedilmiş" sayılan kalan bakiye, bonusa esas
// yatırımın yüzdesidir. Ölçü sunucudakiyle aynıdır
// (services/bonus_eligibility.lost_deposit_limit): kolon eklenmeden önce
// kaydedilmiş kampanyada alan NULL gelir, varsayılan %10'dur.
export const LOST_DEPOSIT_DEFAULT_PERCENT = 10;

export const lostDepositPercent = (bonus) => {
  const raw = bonus?.lost_deposit_max_percent;
  if (raw === null || raw === undefined || raw === '') return LOST_DEPOSIT_DEFAULT_PERCENT;
  const number = Number(raw);
  return Number.isFinite(number) ? number : LOST_DEPOSIT_DEFAULT_PERCENT;
};

// Çekim kuralının ölçüsü (services/bonus_eligibility.withdraw_block_mode):
// 'net' = çekim yatırımı aşarsa ya da net giriş tutarın altındaysa alamaz,
// 'any' = tek bir çekim bile engeller. Kolon eklenmeden önce kaydedilmiş
// kampanyada alan gelmez; varsayılan 'net'tir.
export const WITHDRAW_BLOCK_DEFAULT_HOURS = 24;

export const withdrawBlockMode = (bonus) => (
  bonus?.withdraw_block_mode === 'any' ? 'any' : 'net'
);

export const withdrawBlockText = (bonus) => {
  const hours = Number(bonus?.withdraw_block_hours) || 0;
  if (hours <= 0) return '';
  if (withdrawBlockMode(bonus) === 'any') return `Son ${hours} saatte çekimi olan alamaz`;
  const minimum = Number(bonus?.withdraw_block_min_net) || 0;
  return minimum > 0
    ? `Son ${hours} saatte net giriş (yatırım − çekim) en az ${minimum.toLocaleString('tr-TR')} ₺`
    : `Son ${hours} saatte çekimi yatırımını aşan alamaz`;
};

export const eligibilityRuleLines = (bonus) => {
  if (!bonus) return [];
  const lines = [];
  const trial = !!bonus.is_trial_bonus;
  const firstCount = Number(bonus.first_deposits_count) || 0;
  if (!trial && firstCount > 0) lines.push(`İlk ${firstCount} yatırıma, her birine ayrı`);
  if (!trial && ruleFlag(bonus, 'daily_first_deposit')) lines.push('Yalnızca günün ilk yatırımı');
  if (weekdaysText(bonus.active_weekdays)) lines.push(`Geçerli günler: ${weekdaysText(bonus.active_weekdays)}`);
  if (withdrawBlockText(bonus)) lines.push(withdrawBlockText(bonus));
  if (!trial && ruleFlag(bonus, 'stop_after_withdraw')) lines.push('İlk yatırımdan sonra çekim yapan alamaz');
  if (!trial && ruleFlag(bonus, 'exclusive_deposit')) lines.push('Yatırıma başka bonus verilmez');
  if (!trial && ruleFlag(bonus, 'blocks_loss_bonus')) lines.push('Yatırımı kayıp bonusuna girmez');
  if (!trial && ruleFlag(bonus, 'require_lost_deposit')) {
    lines.push(`İade: yatırım kaybedilince alınır (kalan bakiye ≤ %${lostDepositPercent(bonus)})`);
  }
  if (trial && ruleFlag(bonus, 'require_no_finance_history')) lines.push('Yatırım/çekim geçmişi olan alamaz');
  if (ruleFlag(bonus, 'block_if_promo_used')) lines.push('Promosyon kodu kullanan alamaz');
  if (ruleFlag(bonus, 'one_per_ip')) lines.push('Aynı IP’den tek üyelik');
  if (bonus.auto_award && ruleFlag(bonus, 'tier_exact')) lines.push('Kademe tutarı birebir eşleşmeli');
  return lines;
};

// Sunucudaki denetimle aynı (services/telegram_link.channel_from_url):
// yalnızca herkese açık kanal/grup adresi doğrulanabilir; davet bağlantısının
// (t.me/+… , joinchat) kullanıcı adı yoktur, bot üyeliği onunla soramaz.
const TELEGRAM_CHANNEL_RE = /^(?:https?:\/\/)?(?:t\.me|telegram\.me)\/([A-Za-z][A-Za-z0-9_]{3,31})\/?$/i;

/** Kanal adresinin hata metni; boş adres ya da geçerli adres için ''. */
export const telegramChannelProblem = (url) => {
  const text = String(url || '').trim();
  if (!text) return '';
  if (/\/(\+|joinchat\/)/i.test(text)) {
    return 'Davet bağlantısı (t.me/+… ) doğrulanamaz. Kanalın herkese açık adresini yazın (https://t.me/KanalAdiniz).';
  }
  return TELEGRAM_CHANNEL_RE.test(text)
    ? ''
    : 'Kanal adresi https://t.me/KanalAdiniz biçiminde olmalıdır.';
};
